import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

export const fetchAdConfigs = createAsyncThunk(
  "adConfig/fetchAdConfigs",
  async ({ search, page, limit } = {}, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `/ad-configs?search=${search || ""}&page=${page || 1}&limit=${limit || 20}`,
        authHeaders()
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const addAdConfig = createAsyncThunk(
  "adConfig/addAdConfig",
  async (data, { rejectWithValue }) => {
    try {
      const response = await Axios.post("/ad-configs", data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateAdConfig = createAsyncThunk(
  "adConfig/updateAdConfig",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/ad-configs/${id}`, data, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteAdConfig = createAsyncThunk(
  "adConfig/deleteAdConfig",
  async (id, { rejectWithValue }) => {
    try {
      await Axios.delete(`/ad-configs/${id}`, authHeaders());
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const changeAdConfigStatus = createAsyncThunk(
  "adConfig/changeStatus",
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      await Axios.patch(`/ad-configs/${id}/status`, { is_active }, authHeaders());
      return { id, is_active };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const adConfigSlice = createSlice({
  name: "adConfig",
  initialState: {
    adConfigs: null,
    isLoading: false,
    updateLoading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchAdConfigs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAdConfigs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adConfigs = action.payload.data;
      })
      .addCase(fetchAdConfigs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add
      .addCase(addAdConfig.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(addAdConfig.fulfilled, (state, action) => {
        state.updateLoading = false;
        if (state.adConfigs?.data) {
          state.adConfigs.data = [action.payload.data, ...state.adConfigs.data];
          state.adConfigs.pagination.total += 1;
        }
      })
      .addCase(addAdConfig.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      // Update
      .addCase(updateAdConfig.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateAdConfig.fulfilled, (state, action) => {
        state.updateLoading = false;
        if (state.adConfigs?.data) {
          const index = state.adConfigs.data.findIndex((item) => item.id === action.payload.data.id);
          if (index !== -1) {
            state.adConfigs.data[index] = action.payload.data;
          }
        }
      })
      .addCase(updateAdConfig.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })
      // Status
      .addCase(changeAdConfigStatus.fulfilled, (state, action) => {
        if (state.adConfigs?.data) {
          const item = state.adConfigs.data.find((i) => i.id === action.payload.id);
          if (item) item.is_active = action.payload.is_active;
        }
      })
      // Delete
      .addCase(deleteAdConfig.fulfilled, (state, action) => {
        if (state.adConfigs?.data) {
          state.adConfigs.data = state.adConfigs.data.filter((i) => i.id !== action.payload);
          state.adConfigs.pagination.total -= 1;
        }
      });
  },
});

export default adConfigSlice.reducer;
